import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { UserCog, Loader2 } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  { key: "revenue", label: "Revenue" },
  { key: "sales", label: "Sales" },
  { key: "customer_success", label: "Customer Success" },
  { key: "support", label: "Support" },
  { key: "marketing", label: "Marketing" },
];

export default function AreaOwnersCard() {
  const [owners, setOwners] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/company/area-owners`)
      .then((r) => { setOwners(r.data.owners); setForm(r.data.owners); })
      .catch(() => toast.error("Failed to load area owners"));
  }, []);

  const dirty = owners && FIELDS.some((f) => (form[f.key] || "") !== (owners[f.key] || ""));

  const save = async () => {
    setBusy(true);
    try {
      const r = await axios.put(`${API}/company/area-owners`, form);
      setOwners(r.data.owners); setForm(r.data.owners);
      toast.success("Area owners saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
    finally { setBusy(false); }
  };

  return (
    <Card className="border-[#DAD5CA] shadow-none" data-testid="admin-area-owners">
      <CardContent className="p-5">
        <div className="flex items-center gap-2">
          <UserCog className="w-4 h-4 text-[#8A857A]" />
          <span className="text-[14px] font-bold text-[#1A1A1A]" style={{ fontFamily: "Manrope, sans-serif" }}>Company Overview — area owners</span>
        </div>
        <p className="mt-1 text-[13px] text-[#59554D]">
          The person named against each area on Company → Overview. Changing a name here changes it on the Overview page for everyone.
        </p>
        {!owners ? (
          <div className="mt-4 h-[120px] bg-[#F6F4EF] rounded animate-pulse" />
        ) : (
          <>
            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {FIELDS.map((f) => (
                <div key={f.key} className="min-w-0">
                  <label className="text-[11.5px] font-semibold text-[#59554D]">{f.label}</label>
                  <Input value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                         className="mt-1 h-9 border-[#DAD5CA]" data-testid={`area-owner-${f.key}`} placeholder="Owner name" />
                </div>
              ))}
            </div>
            <div className="mt-4 flex items-center gap-3">
              <Button onClick={save} disabled={!dirty || busy} data-testid="area-owners-save"
                      className="h-9 bg-[#FFD108] hover:bg-[#E0B800] text-[#121212] font-semibold disabled:bg-[#E6E2D9] disabled:text-[#B5AFA3]">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save area owners"}
              </Button>
              {!dirty && <span className="text-[12px] text-[#8A857A]">Nothing to save.</span>}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
